/** Dashboard auth client for the D-175 auth Worker.
 *
 * Browser JS never receives Supabase session material. The Worker owns the
 * encrypted HttpOnly cookie and returns only display-safe metadata plus CSRF.
 */

export interface AuthUser {
  id: string;
  email: string;
  created_at: string;
}

export interface AuthSession {
  user: AuthUser;
  expiresAt: number;
  csrfToken: string;
}

interface SessionWire {
  authenticated: boolean;
  user: AuthUser | null;
  expiresAt?: number;
  csrfToken?: string;
}

const LEGACY_SESSION_KEY = 'recued.auth.session';

export interface AuthClient {
  signUp(email: string, password: string): Promise<AuthSession>;
  signIn(email: string, password: string): Promise<AuthSession>;
  /** Resend the signup confirmation email. Rejects with an `AuthRequestError`
   *  (`code: 'resend_rate_limited'`) when GoTrue's send rate limit is hit. */
  resendConfirmation(email: string): Promise<void>;
  signInWithOAuth(provider: 'google' | 'github'): Promise<void>;
  signOut(): Promise<void>;
  getUser(): Promise<AuthUser | null>;
  getSession(): Promise<AuthSession | null>;
  getAccessToken(): Promise<null>;
  handleOAuthCallback(): Promise<AuthSession | null>;
  requestPasswordReset(email: string): Promise<void>;
  updatePassword(newPassword: string): Promise<AuthSession>;
  handleRecoveryCallback(): { mode: 'reset-update' } | null;
  /** SW-8 — the user returned from an email-confirmation link
   *  (`?auth=confirmed`). Cleans the param and signals a friendly
   *  "email confirmed, sign in" landing. */
  handleEmailConfirmedCallback(): { mode: 'email-confirmed' } | null;
  /** DB-7a — the user returned from the Paddle checkout / customer portal
   *  (`?checkout=success|cancelled`). Cleans the param and reports the
   *  outcome so the caller can refresh the plan + confirm without a reload. */
  handleBillingReturn(): { checkout: 'success' | 'cancelled' } | null;
  request<T>(path: string, init?: RequestInit): Promise<T>;
}

/** The billing-return contract, in one place because it has TWO ends that must
 *  agree and cannot see each other: `handleBillingReturn` below parses it, and
 *  `public/checkout/index.html` — a hand-written page outside the TS build —
 *  constructs it after Paddle's overlay closes. Rename the param here and the
 *  page keeps redirecting with the old one; the banner would simply never fire,
 *  silently, on the one flow a paying customer takes.
 *  Joined by src/__tests__/checkout-page.test.ts. */
export const BILLING_RETURN_PARAM = 'checkout';
export const BILLING_RETURN_VALUES = ['success', 'cancelled'] as const;
export type BillingReturnValue = (typeof BILLING_RETURN_VALUES)[number];

const normalizeError = (data: unknown, fallback: string): string => {
  if (data && typeof data === 'object') {
    const record = data as Record<string, unknown>;
    const error = record.error && typeof record.error === 'object'
      ? record.error as Record<string, unknown>
      : null;
    const message = error?.message ?? record.message;
    if (typeof message === 'string' && message.trim()) return message.trim();
  }
  return fallback;
};

/** The machine `code` from a `{ error: { code } }` body, or '' when absent. */
const errorCode = (data: unknown): string => {
  if (data && typeof data === 'object') {
    const error = (data as Record<string, unknown>).error;
    if (error && typeof error === 'object') {
      const code = (error as Record<string, unknown>).code;
      if (typeof code === 'string') return code;
    }
  }
  return '';
};

/** A non-2xx from the auth Worker, carrying the machine `code` so callers can
 *  branch on it. `code: 'no_session'` after signup / signin is NOT a failure —
 *  it means the account exists but email confirmation is still pending. */
export class AuthRequestError extends Error {
  readonly code: string;
  readonly status: number;
  constructor(message: string, code: string, status: number) {
    super(message);
    this.name = 'AuthRequestError';
    this.code = code;
    this.status = status;
  }
}

const clearLegacySessionStorage = (): void => {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.removeItem(LEGACY_SESSION_KEY);
  } catch {
    // Browsers can block storage access; the new client never reads it.
  }
};

export const createAuthClient = (workerUrl: string): AuthClient => {
  clearLegacySessionStorage();
  const baseUrl = workerUrl.replace(/\/$/, '');
  let currentSession: AuthSession | null = null;
  let csrfToken: string | null = null;

  const readJson = async (res: Response): Promise<unknown> => {
    const text = await res.text();
    if (!text) return {};
    try { return JSON.parse(text); } catch { return { message: text }; }
  };

  const rememberSession = (wire: SessionWire): AuthSession | null => {
    if (wire.csrfToken) csrfToken = wire.csrfToken;
    if (!wire.authenticated || !wire.user) {
      currentSession = null;
      return null;
    }
    currentSession = {
      user: wire.user,
      expiresAt: wire.expiresAt ?? 0,
      csrfToken: wire.csrfToken ?? csrfToken ?? '',
    };
    return currentSession;
  };

  const fetchSession = async (): Promise<AuthSession | null> => {
    const res = await fetch(`${baseUrl}/v1/auth/session`, {
      method: 'GET',
      credentials: 'include',
      headers: { 'Accept': 'application/json' },
    });
    const data = await readJson(res);
    if (!res.ok) throw new Error(normalizeError(data, 'Session check failed'));
    return rememberSession(data as SessionWire);
  };

  const ensureCsrf = async (): Promise<void> => {
    if (!csrfToken) await fetchSession();
    if (!csrfToken) throw new Error('Auth form expired. Please retry.');
  };

  const request = async <T>(path: string, init: RequestInit = {}): Promise<T> => {
    const method = (init.method ?? 'GET').toUpperCase();
    const headers = new Headers(init.headers);
    headers.set('Accept', 'application/json');

    if (init.body && !headers.has('Content-Type')) {
      headers.set('Content-Type', 'application/json');
    }
    if (method !== 'GET' && method !== 'HEAD' && method !== 'OPTIONS') {
      await ensureCsrf();
      headers.set('X-CSRF-Token', csrfToken ?? '');
    }

    const res = await fetch(`${baseUrl}${path}`, {
      ...init,
      method,
      headers,
      credentials: 'include',
    });
    const data = await readJson(res);
    if (!res.ok) throw new AuthRequestError(normalizeError(data, 'Request failed'), errorCode(data), res.status);
    if (data && typeof data === 'object' && 'csrfToken' in data) {
      const maybeSession = data as Partial<SessionWire>;
      if (typeof maybeSession.csrfToken === 'string') csrfToken = maybeSession.csrfToken;
      if (typeof maybeSession.authenticated === 'boolean') rememberSession(maybeSession as SessionWire);
    }
    return data as T;
  };

  const authRequest = async (
    path: string,
    email: string,
    password: string,
    extra?: Record<string, unknown>,
  ): Promise<AuthSession> => {
    const data = await request<SessionWire>(path, {
      method: 'POST',
      body: JSON.stringify({ email, password, ...extra }),
    });
    const session = rememberSession(data);
    if (!session) throw new Error('Sign in required.');
    return session;
  };

  const currentUrl = (): string => {
    if (typeof window === 'undefined') return '/';
    return `${window.location.origin}${window.location.pathname}${window.location.search}${window.location.hash}`;
  };

  const cleanCallbackParams = (): void => {
    if (typeof window === 'undefined') return;
    const url = new URL(window.location.href);
    url.searchParams.delete('auth_error');
    url.searchParams.delete('auth');
    url.searchParams.delete(BILLING_RETURN_PARAM);
    window.history.replaceState(null, '', `${url.pathname}${url.search}${url.hash}`);
  };

  return {
    // The UI gates signUp behind the agree-to-terms checkbox (disabled button
    // + submit guard); the server independently ENFORCES + records this flag,
    // so the dashboard asserts agreement here and a non-dashboard client must
    // do the same (D-176 server-side Terms gate).
    signUp: (email, password) => authRequest('/v1/auth/signup', email, password, { agreed_terms: true }),
    signIn: (email, password) => authRequest('/v1/auth/signin', email, password),

    async resendConfirmation(email) {
      await request('/v1/auth/resend', { method: 'POST', body: JSON.stringify({ email }) });
    },

    signInWithOAuth(provider) {
      if (typeof window === 'undefined') return Promise.resolve();
      const url = new URL(`${baseUrl}/v1/auth/oauth/start`);
      url.searchParams.set('provider', provider);
      url.searchParams.set('return_to', currentUrl());
      window.location.href = url.toString();
      return Promise.resolve();
    },

    async signOut() {
      try {
        await request('/v1/auth/signout', { method: 'POST' });
      } finally {
        currentSession = null;
      }
    },

    async getUser() {
      return (await fetchSession())?.user ?? null;
    },

    getSession: fetchSession,

    async getAccessToken() {
      return null;
    },

    async handleOAuthCallback() {
      if (typeof window !== 'undefined') {
        const params = new URLSearchParams(window.location.search);
        const err = params.get('auth_error');
        if (err) {
          cleanCallbackParams();
          throw new Error(err);
        }
      }
      return fetchSession();
    },

    async requestPasswordReset(email: string) {
      await request('/v1/auth/password/reset', {
        method: 'POST',
        body: JSON.stringify({ email, returnTo: currentUrl() }),
      });
    },

    async updatePassword(newPassword: string) {
      const data = await request<SessionWire>('/v1/auth/password/update', {
        method: 'POST',
        body: JSON.stringify({ password: newPassword }),
      });
      const session = rememberSession(data);
      if (!session) throw new Error('Sign in required.');
      return session;
    },

    handleRecoveryCallback() {
      if (typeof window === 'undefined') return null;
      const params = new URLSearchParams(window.location.search);
      if (params.get('auth') !== 'reset') return null;
      cleanCallbackParams();
      return { mode: 'reset-update' };
    },

    handleEmailConfirmedCallback() {
      if (typeof window === 'undefined') return null;
      const params = new URLSearchParams(window.location.search);
      if (params.get('auth') !== 'confirmed') return null;
      cleanCallbackParams();
      return { mode: 'email-confirmed' };
    },

    handleBillingReturn() {
      if (typeof window === 'undefined') return null;
      const params = new URLSearchParams(window.location.search);
      const checkout = params.get(BILLING_RETURN_PARAM);
      if (!BILLING_RETURN_VALUES.includes(checkout as BillingReturnValue)) return null;
      cleanCallbackParams();
      return { checkout: checkout as BillingReturnValue };
    },

    request,
  };
};
